'use client';
import Eyebrow from 'components/shared/Eyebrow/Eyebrow';
import HeroWrapper from 'components/HeroConsolidated/shared/HeroWrapper';
import RichTextList from '../shared/RichTextList/RichTextList';
import Button from '../shared/Buttons/BaseButton';
import Media from 'components/shared/Media/Media';
import { FullBleedVideoPlayer } from 'components/VideoPlayer';
import { HeroConsolidatedProps } from './types';
import { CSSProperties, useRef } from 'react';
import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useRouter, useParams } from 'next/navigation';
import { useGreenhouse } from '@/contexts/GreenhouseProvider';

interface HeroCareersFiltersIdProps {
  id: string;
}

const HeroCareersFilters = ({ id }: HeroCareersFiltersIdProps) => {
  const { jobLocations } = useGreenhouse();
  const keyword = useRef<HTMLInputElement>(null);
  const location = useRef<HTMLSelectElement>(null);
  const router = useRouter();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const searchParams = new URLSearchParams();
    if (keyword.current?.value) searchParams.set('keyword', keyword.current.value);
    if (location.current?.value) searchParams.set('location', location.current.value);
    router.push(`/jobs/?${searchParams.toString()}`);
  };

  return (
    <form
      data-testid="form"
      className="flex flex-col md:flex-row gap-2 sm:max-w-[400px] md:max-w-full"
      onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor={`${id}-jobs`}>
        Search Jobs
      </label>
      <input
        ref={keyword}
        placeholder="Search Jobs"
        className="block w-full md:max-w-[210px] white-colorway font-semibold rounded-md hover:cursor-pointer placeholder:text-(--kale)"
        id={`${id}-jobs`}
      />
      <label className="sr-only" htmlFor={`${id}-locations`}>
        Filter by Location
      </label>
      <select
        ref={location}
        className="block w-full md:max-w-[210px] white-colorway font-semibold rounded-md hover:cursor-pointer"
        id={`${id}-locations`}>
        <option value="">All Locations</option>
        {jobLocations.map(loc => (
          <option key={loc} value={loc}>
            {loc}
          </option>
        ))}
      </select>
      <Button>Submit</Button>
    </form>
  );
};

const HeroCareersVariant = ({
  desktopMedia,
  mobileMedia,
  color,
  copy,
  sys,
  margins,
  sectionName,
  eyebrowAlternateLogo,
  eyebrowChimeLogo,
  eyebrowText,
  video,
  className,
  innerClassName,
  mediaHasOverlay,
  heroIsScreenHeight,
}: HeroConsolidatedProps) => {
  const hasVideo = video != null && video.video != null; // Linked videos are not supported by this module variant
  const desktopUrl = hasVideo ? video.previewImage.url : desktopMedia?.url;
  const mobileUrl = hasVideo ? video.previewImage.url : (mobileMedia?.url ?? desktopMedia?.url);
  const primaryMedia = desktopMedia ?? mobileMedia;
  const hasMedia = primaryMedia != null;
  const useBackgroundImage = !mediaHasOverlay || hasVideo;

  return (
    <div
      style={
        {
          '--image-url': useBackgroundImage ? `url(${desktopUrl})` : '',
          '--mobile-image-url': useBackgroundImage ? `url(${mobileUrl})` : '',
        } as CSSProperties
      }
      className={cn(
        getColorway(color),
        'bg-transparent', // Transparent background so the play button gets right color
        'hero-container md:min-h-125 max-sm:min-h-150 w-full h-full',
        'bg-(image:--mobile-image-url) sm:bg-(image:--image-url) bg-center bg-cover relative',
        { 'pb-6 sm:pb-0': mediaHasOverlay },
        className,
      )}>
      {(hasVideo || mediaHasOverlay) && (
        <div className={cn('absolute w-full h-full overflow-hidden')}>
          {mediaHasOverlay && <div className={cn(getColorway(color), 'absolute top-0 left-0 w-full h-full')}></div>}
          {hasVideo && <FullBleedVideoPlayer video={video} mediaHasOverlay={mediaHasOverlay} />}
          {hasMedia && !hasVideo && (
            <div className="h-full opacity-15">
              <Media
                media={primaryMedia}
                mobileMedia={mobileMedia}
                sizes={'100vw'}
                className="w-full h-full rounded-none"
              />
            </div>
          )}
        </div>
      )}
      <HeroWrapper
        testId={'careerVariant'}
        className={cn(
          'bg-inherit drop-shadow-[0px_4px_4px_rgba(5,35,22,0.75)] max-sm:min-h-150 flex',
          { 'md:min-h-[84vh] md:items-center': heroIsScreenHeight },
          innerClassName,
        )}
        color={color}
        margins={margins}
        sectionName={sectionName}
        innerClassName={'sm:flex-col md:flex-row sm:gap-4 md:gap-8 lg:gap-16'}>
        <div className="flex flex-col md:gap-12">
          <div className="flex flex-col-reverse">
            <h1 data-testid="headline" className="text-heading-xxl md:text-heading-xxxxl lg:text-[160px]">
              Join <span className="md:whitespace-nowrap">the</span>{' '}
              <span className="md:whitespace-nowrap">
                <span className="text-(--color-fern)">Chime</span> team
              </span>
            </h1>
            <Eyebrow
              eyebrowAlternateLogo={eyebrowAlternateLogo}
              eyebrowChimeLogo={eyebrowChimeLogo}
              eyebrowText={eyebrowText}
              color={color}
              isHeading={true}
              sys={sys}
            />
          </div>
          <div className="hidden md:block">
            <HeroCareersFilters id="desktop" />
          </div>
        </div>
        <div className="flex flex-col gap-4 sm:gap-12">
          {copy && (
            <div
              className={cn('hero-copy font-serif text-body-serif sm:max-w-[600px]')}
              {...liveUpdateAttrs(sys?.id, 'copy')}>
              <RichTextList item={copy} />
            </div>
          )}
          <div className="md:hidden">
            <HeroCareersFilters id="mobile" />
          </div>
        </div>
      </HeroWrapper>
    </div>
  );
};

export default HeroCareersVariant;
